import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button, Form, Col, Row, Icon, Steps, notification, Alert } from "antd";
import { withRouter } from "react-router-dom";
import api from "../../../services/api";
import { getCompanyCurrentUser } from "../../../services/auth";
import {
  COUPON_CODE_TYPE,
  COUPON_TYPE,
  COUPON_VALUE_TYPE,
  COUPONS_USAGE_AMOUNT_RULES,
} from "../../../services/utils";
import { useFreire } from "../../../utils/freireContext";
import * as texts from "../locales";
import { useOnMount } from "../../../hooks";
import {
  formatAvailableCompanies,
  formatDuration,
  formatUsageLimitPerPeriod,
} from "../utils";
import "./index.css";
import CampaignInfo from "./Forms/CampaignInfo";
import ValidPlaces from "./Forms/ValidPlaces";
import Notification from "./Forms/Notification";
import { NewCampaignContext } from "./context";
import CampaignQuota from "./Forms/CampaignQuota";
import CouponCampaignRules from "./Forms/CouponCampaignRules";
import EligibleUsers, {
  ELIGIBLE_USERS_TO_USE_COUPON_TYPE,
  autoLinkCouponType,
} from "./Forms/EligibleUsers";
import * as XLSX from "xlsx";
import { wordsToExcludeFromCouponSheet } from "../utils";
import Breadcrumbs from "../../../components/Breadcrumbs";
import * as dashboardTexts from "../../Dashboard/locales";
import { FleetInstructionsAlert } from "../../../components/FleetInstructions/fleetAlert";
import { ChargePayer } from "../../../constants/charge-payer";

const { Step } = Steps;

const { PERCENTAGE, FIXED_VALUE, CREDIT } = COUPON_TYPE;

export const COUPON_LINK_RULES = {
  ONLY_USERS_WITHOUT_CHARGE: "onlyUsersWithoutCharge",
  ALL_USERS: "allUsers",
};

const NewCoupons = ({ form, history }) => {
  const { freire } = useFreire();
  const { state } = history.location;
  const isFleetsRoute = history.location?.pathname?.includes("/fleets");

  const setFormFieldIniitalValue = () => {
    const fieldsToFillIn = [
      "benefit.value",
      "benefit.type",
      "campaignPartners",
      "company",
      "couponDuration.enabled",
      "couponDuration.value",
      "couponDuration.unit",
      "message",
      "quota.enabled",
      "quota.type",
      "quota.totalBalance",
      "quota.accumulateToNextPeriod",
      "quota.couponsExpireOnTotalUsage",
      "theme.enabled",
      "theme.alias",
      "usageLimitPerPeriod",
      "usageAmountRule",
      "validPlace",
      "amountOfCoupons",
      "title",
      "codeType",
      "cod",
      "validOnlyInOwnWhitelabel",
    ];

    fieldsToFillIn.forEach((field) => {
      const value = field
        .split(".")
        .reduce(
          (o, i) => (o && o[i] !== undefined ? o[i] : undefined),
          state.campaign
        );
      form.getFieldDecorator(field, { initialValue: value ?? undefined });
    });
  };

  if (state?.campaign) setFormFieldIniitalValue();

  const [loadingSave, setLoadingSave] = useState(false);
  const [userCompany, setUserCompany] = useState("");
  const [allCompanies, setAllCompanies] = useState([]);
  const [allStations, setAllStations] = useState([]);
  const [whiteLabel, setWhiteLabel] = useState("");

  const getAllCompanies = async () => {
    const { data } = await api.get("company");

    setAllCompanies(data);
  };

  const getAllStations = async () => {
    // include_ocpi: as estações de roaming vêm do OCPI, com a party correta.
    // O espelho no Mongo não distingue duas parties que publicam o mesmo EVSE.
    const { data } = await api.get(`select/connected_stations`, {
      params: { include_ocpi: true },
    });

    setAllStations(data);
  };

  const getUserCompany = async () => {
    const company = await getCompanyCurrentUser();

    setUserCompany(company);
  };

  useOnMount(() => {
    getUserCompany();
    getAllCompanies();
    getAllStations();
  });

  const { company: companyId } = form.getFieldsValue();

  const selectedCompany = allCompanies.find(
    (company) => company._id === companyId
  );

  const selectedParentCompany = allCompanies.find(
    (company) => company._id === selectedCompany?.parentCompanyID
  );

  useEffect(() => {
    if (!selectedCompany?.whiteLabelAlias && selectedCompany?.parentCompanyID) {
      const fetchData = async () => {
        const { data: parentCompany } = await api.get(
          `company/${selectedCompany.parentCompanyID}`
        );
        setWhiteLabel(parentCompany?.whiteLabelAlias);
      };
      if (selectedParentCompany) {
        setWhiteLabel(selectedParentCompany.whiteLabelAlias);
      } else {
        fetchData();
      }
      return;
    }

    setWhiteLabel(selectedCompany?.whiteLabelAlias);
  }, [selectedCompany, selectedParentCompany]);

  const validateAndTransformParams = async () => {
    return new Promise((resolve, reject) => {
      form.validateFieldsAndScroll(async (error, params) => {
        if (error) {
          reject(error);
          return;
        }

        if (params.benefit.type === PERCENTAGE) {
          params.benefit.value = (
            parseFloat(params.benefit.value.replace(/[,]/g, ".")) / 100
          ).toFixed(4);
        } else if (params.benefit.type === FIXED_VALUE) {
          params.benefit.value = parseInt(
            params.benefit.value.replace(/[.,]/g, "")
          );
        } else if (params.benefit.type === CREDIT) {
          let totalBalance = parseInt(
            params.benefit.value.replace(/[.,]/g, ""),
            10
          );

          if (params.benefit.valueType === COUPON_VALUE_TYPE.KWH) {
            totalBalance = totalBalance / 100;
          }

          params.codeType = COUPON_CODE_TYPE.DIFFERENT;
          params.totalBalance = totalBalance;
          params.benefit.value = totalBalance;
        }

        params.expireDate = params.expireDate.toISOString();

        if (params.usageAmountRule === COUPONS_USAGE_AMOUNT_RULES.UNLIMITED) {
          params.usageLimitPerCoupon = -1;
        }

        if (
          params.usageAmountRule === COUPONS_USAGE_AMOUNT_RULES.FIRST_CHARGE
        ) {
          params.usageLimitPerCoupon = -1;
          params.isFirstChargeCoupon = true;
        }

        if (params.onlyUsersWithoutCharge) {
          params.couponLinkRule = COUPON_LINK_RULES.ONLY_USERS_WITHOUT_CHARGE;
        }

        if (!params.company) {
          params.company = userCompany.id;
        }

        if (!params.usageLimitPerPeriod?.enabled) {
          params.usageLimitPerPeriod = undefined;
        } else {
          params.usageLimitPerPeriod = formatUsageLimitPerPeriod(
            params.usageLimitPerPeriod
          );
        }

        if (!params.quota?.enabled) {
          params.quota = undefined;
        } else {
          delete params.quota.enabled;
          const formattedTotalBalance = parseInt(
            params.quota.totalBalance.replace(/[.,]/g, "")
          );

          params.quota.totalBalance = formattedTotalBalance;
          params.quota.accumulateToNextPeriod =
            params.quota?.accumulateToNextPeriod || false;
          params.quota.couponsExpireOnTotalUsage =
            params.quota?.couponsExpireOnTotalUsage || false;
        }

        if (
          params.eligibleUsersType ===
          ELIGIBLE_USERS_TO_USE_COUPON_TYPE.ELIGIBLE_USERS
        ) {
          params.codeType = COUPON_CODE_TYPE.AUTOLINKING;
        }

        if (params.hasPercentageBalanceLimitPerCharge) {
          params.percentageBalanceLimitPerCharge = Number(
            params.percentageBalanceLimitPerCharge
          );
        }

        if (params.validOnlyInOwnWhitelabel) {
          params.allowed_white_labels = [whiteLabel];
        }

        delete params.eligibleUsersType;
        delete params.hasPercentageBalanceLimitPerCharge;
        delete params.usageAmountRule;
        delete params.validOnlyInOwnWhitelabel;

        resolve(params);
      });
    });
  };

  const createCampaignCoupon = async () => {
    try {
      const params = await validateAndTransformParams();

      if (!params) return;

      const availableCompanies = formatAvailableCompanies(params, userCompany, {
        allStations,
        allCompanies,
      });

      if (!availableCompanies) {
        notification.warning({
          message: freire(texts.ATTENTION),
          description: freire(texts.SPECIFIC_STATIONS_ERROR_MESSAGE),
        });
        return;
      }

      params.availableCompanies = availableCompanies;

      params.couponDuration = formatDuration(params.couponDuration);

      params.renewalTime = formatDuration(params.renewalTime);

      params.accumulateOnRenewal = params.accumulateOnRenewal || false;

      if (params.couponsSheet) {
        const file = params.couponsSheet.fileList[0].originFileObj;

        const data = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsBinaryString(file);
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = (e) => reject(e.target.error);
        });

        const workbook = XLSX.read(data, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const sheetData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        const coupons = [
          ...new Set(
            sheetData
              .map((row) => {
                if (
                  params.autoLinkCouponType === autoLinkCouponType.CHASSI ||
                  params.autoLinkCouponType === autoLinkCouponType.CPF
                ) {
                  return String(row[0])
                    .toUpperCase()
                    .replace(/[^a-zA-Z0-9]/g, "")
                    .trim();
                } else {
                  return String(row[0]).toUpperCase().trim();
                }
              })
              .filter((cod) => !cod.match(new RegExp("undefined", "i")))
              .filter((cod) => {
                if (params.autoLinkCouponType === autoLinkCouponType.CHASSI) {
                  return !new RegExp(
                    wordsToExcludeFromCouponSheet.join("|"),
                    "gi"
                  ).test(cod);
                }

                if (params.autoLinkCouponType === autoLinkCouponType.EMAIL) {
                  return new RegExp("@", "gi").test(cod);
                }

                if (params.autoLinkCouponType === autoLinkCouponType.CPF) {
                  return /^[0-9]{11}$/.test(cod);
                }

                return true;
              })
          ),
        ];

        params.autoLinkedCoupons = coupons;
        params.amountOfCoupons = coupons.length;
        delete params.couponsSheet;
      }

      setLoadingSave(true);

      params.payer = isFleetsRoute && !params.payer
        ? ChargePayer.CPO
        : ChargePayer.USER;

      await api.post("campaigns", params);

      notification.success({
        message: freire(texts.ALL_RIGHT),
        description: freire(texts.CREATED_CAMPAIGN_SUCCESSFULLY),
      });

      setLoadingSave(false);

      history.push(
        state?.returnTo ||
          (isFleetsRoute ? "/dashboard/fleets" : "/dashboard/coupons")
      );
    } catch (error) {
      if (!axios.isAxiosError(error)) return;

      setLoadingSave(false);

      notification.error({
        message: freire(texts.WENT_WRONG),
        description: error.response?.data?.message || error.message,
      });
    }
  };

  const { benefit } = form.getFieldsValue();

  return (
    <NewCampaignContext.Provider
      value={{
        form,
        userCompany,
        allCompanies,
        allStations,
        selectedCompany,
        isFleetsRoute,
      }}
    >
      <div className="container">
        {isFleetsRoute ? (
          <Breadcrumbs
            breadcrumbs={[
              freire(dashboardTexts.ADMIN),
              freire(dashboardTexts.FLEETS),
              freire(dashboardTexts.COUPONS),
            ]}
          />
        ) : null}
        <div className="content" style={{ height: "auto" }}>
          <Row>
            <Col span={6}>
              <Steps current={1} size="small" direction="vertical">
                <Step
                  icon={<Icon type="profile" />}
                  title={freire(texts.NEW_CAMPAIGN)}
                  description={freire(
                    isFleetsRoute
                      ? texts.NEW_FLEET_CAMPAIGN_DESCRIPTION
                      : texts.NEW_CAMPAIGN_DESCRIPTION
                  )}
                />
              </Steps>
            </Col>

            <Col span={18} style={{ maxWidth: 1000 }}>
              {isFleetsRoute && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                    marginBottom: 24,
                  }}
                >
                  <span style={{ color: "#fd5876", fontWeight: "bold" }}>
                    {freire(texts.HOW_TO_CREATE_CAMPAIGN)}
                  </span>
                  <div>{freire(texts.CREATE_CAMPAIGN_INSTRUCTIONS)}</div>
                  <FleetInstructionsAlert />
                </div>
              )}

              <Form wrapperCol={{ span: 24 }}>
                <CampaignInfo isFleet={isFleetsRoute} />

                <EligibleUsers isFleet={isFleetsRoute} />

                <CouponCampaignRules isFleet={isFleetsRoute} />

                <CampaignQuota isFleet={isFleetsRoute} showDivider={true} />

                <span style={{ color: "#fd5876", fontWeight: "bold" }}>
                  {freire(texts.VALID_PLACES)}
                </span>
                <p style={{ color: "#747277" }}>
                  {freire(
                    isFleetsRoute
                      ? texts.FLEET_VALID_PLACES_DESCRIPTION
                      : texts.VALID_PLACES_DESCRIPTION
                  )}
                </p>

                {benefit?.valueType === COUPON_VALUE_TYPE.KWH && (
                  <Alert
                    description={
                      <span style={{ whiteSpace: "pre-line" }}>
                        {freire(texts.COUPON_CREDIT_KWH_LOCATION_ALERT)}
                      </span>
                    }
                    type="warning"
                    showIcon
                    style={{ marginBottom: 25 }}
                  />
                )}

                <ValidPlaces />

                {!isFleetsRoute && <Notification showDivider={false} />}
              </Form>

              <div className="footer">
                <Button
                  style={{ marginRight: 20 }}
                  onClick={() =>
                    history.push(
                      state?.returnTo ||
                        (isFleetsRoute
                          ? "/dashboard/fleets"
                          : "/dashboard/coupons")
                    )
                  }
                  type="default"
                >
                  {freire(texts.CANCEL)}
                </Button>
                <Button
                  loading={loadingSave}
                  onClick={createCampaignCoupon}
                  type="primary"
                >
                  {freire(texts.SAVE)}
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </NewCampaignContext.Provider>
  );
};

const PageNewCoupons = Form.create({ name: "inputs" })(NewCoupons);
export default withRouter(PageNewCoupons);
